import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';
import { 
  Users, Search, RefreshCw, IndianRupee, TrendingUp, TrendingDown,
  Phone, Mail, Calendar, FileText, Upload, Eye, ArrowLeft, 
  PieChart, BarChart3, Building2, Wallet, ChevronRight, Loader2
} from 'lucide-react';

export const ClientDashboardPage = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [summary, setSummary] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const params = searchQuery ? { search: searchQuery } : {};
      const [clientsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/portfolio/client-profiles`, { params: { ...params, limit: 100 } }),
        axios.get(`${API}/portfolio/rta-holdings/summary`)
      ]);
      setClients(clientsRes.data.clients || []);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Failed to fetch client data');
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const fetchClientDetails = async (pan) => {
    setLoadingDetails(true);
    try {
      const response = await axios.get(`${API}/portfolio/client/${pan}`);
      setClientDetails(response.data);
      setSelectedClient(pan);
    } catch (error) {
      console.error('Error fetching client details:', error);
      toast.error('Failed to fetch client details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatNumber = (value) => {
    if (!value) return '0';
    return new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: 2
    }).format(value);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="client-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Client Portfolio Dashboard</h1>
            <p className="text-muted-foreground">Manage and view client holdings from RTA imports</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={fetchClients}
              className="border-border"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button 
              onClick={() => navigate('/distributor/cas-import')}
              className="bg-primary hover:bg-primary/90"
              data-testid="import-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import Files
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-primary/20 to-primary/5 border-primary/20">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total AUM</p>
                    <p className="text-2xl font-bold text-white" data-testid="total-aum">
                      {formatCurrency(summary.total_aum)}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center">
                    <Wallet className="w-6 h-6 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Clients</p>
                    <p className="text-2xl font-bold text-white" data-testid="total-clients">
                      {clients.length}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Investors</p>
                    <p className="text-2xl font-bold text-white">
                      {summary.total_investors}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-emerald-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Avg. Portfolio</p>
                    <p className="text-2xl font-bold text-white">
                      {formatCurrency(summary.total_aum / (summary.total_investors || 1))}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-purple-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Search and Client List */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Client Profiles
              </CardTitle>
              <div className="relative w-full sm:w-80">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, PAN, email, mobile..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-secondary border-border"
                  data-testid="search-input"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
            ) : clients.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No clients found</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => navigate('/distributor/cas-import')}
                >
                  Import Client Data
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">Client</th>
                      <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">Contact</th>
                      <th className="text-right py-3 px-4 text-xs font-medium text-muted-foreground uppercase">Holdings</th>
                      <th className="text-right py-3 px-4 text-xs font-medium text-muted-foreground uppercase">Total Value</th>
                      <th className="text-center py-3 px-4 text-xs font-medium text-muted-foreground uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map((client, index) => (
                      <tr 
                        key={client.pan || index} 
                        className="border-b border-border/50 hover:bg-secondary/30 transition-colors cursor-pointer"
                        onClick={() => fetchClientDetails(client.pan)}
                        data-testid={`client-row-${client.pan}`}
                      >
                        <td className="py-4 px-4">
                          <div>
                            <p className="font-medium text-white">{client.name}</p>
                            <p className="text-xs text-muted-foreground">PAN: {client.pan}</p>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="space-y-1">
                            {client.mobile && (
                              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                <Phone className="w-3 h-3" />
                                {client.mobile}
                              </div>
                            )}
                            {client.email && (
                              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                <Mail className="w-3 h-3" />
                                {client.email}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <Badge variant="secondary" className="bg-primary/10 text-primary">
                            {client.total_holdings || 0} schemes
                          </Badge>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <p className="font-semibold text-white">
                            {formatCurrency(client.total_value)}
                          </p>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-primary hover:text-primary/80"
                            onClick={(e) => {
                              e.stopPropagation();
                              fetchClientDetails(client.pan);
                            }}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Client Detail Modal */}
        <Dialog open={!!selectedClient} onOpenChange={() => { setSelectedClient(null); setClientDetails(null); }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-card border-border">
            <DialogHeader>
              <DialogTitle className="text-xl flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Client Portfolio Details
              </DialogTitle>
            </DialogHeader>

            {loadingDetails ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
            ) : clientDetails ? (
              <div className="space-y-6">
                {/* Client Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="bg-secondary/30 border-border">
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-white mb-3">{clientDetails.client.name}</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <FileText className="w-4 h-4" />
                          <span>PAN: {clientDetails.client.pan}</span>
                        </div>
                        {clientDetails.client.mobile && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Phone className="w-4 h-4" />
                            <span>{clientDetails.client.mobile}</span>
                          </div>
                        )}
                        {clientDetails.client.email && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Mail className="w-4 h-4" />
                            <span>{clientDetails.client.email}</span>
                          </div>
                        )}
                        {clientDetails.client.dob && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Calendar className="w-4 h-4" />
                            <span>DOB: {clientDetails.client.dob}</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-primary/20 to-primary/5 border-primary/20">
                    <CardContent className="p-4">
                      <h3 className="text-sm text-muted-foreground mb-2">Portfolio Value</h3>
                      <p className="text-3xl font-bold text-white" data-testid="client-total-value">
                        {formatCurrency(clientDetails.summary.total_value)}
                      </p>
                      <div className="flex items-center gap-4 mt-3 text-sm">
                        <div>
                          <span className="text-muted-foreground">Schemes: </span>
                          <span className="text-white font-medium">{clientDetails.summary.total_schemes}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Units: </span>
                          <span className="text-white font-medium">{formatNumber(clientDetails.summary.total_units)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Scheme-wise Holdings */}
                <Card className="bg-secondary/30 border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <PieChart className="w-4 h-4 text-primary" />
                      Scheme-wise Holdings
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {clientDetails.summary.schemes.map((scheme, index) => (
                        <div 
                          key={index}
                          className="p-4 bg-card rounded-lg border border-border"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-white text-sm line-clamp-2">
                                {scheme.scheme_name}
                              </p>
                              <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                                <span>Folio: {scheme.folios?.join(', ') || 'N/A'}</span>
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <p className="font-semibold text-white">
                                {formatCurrency(scheme.total_value)}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {formatNumber(scheme.total_units)} units
                              </p>
                            </div>
                          </div>
                          {/* Progress bar showing allocation */}
                          <div className="mt-3">
                            <div className="h-2 bg-secondary rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-primary rounded-full transition-all"
                                style={{ 
                                  width: `${(scheme.total_value / clientDetails.summary.total_value * 100).toFixed(1)}%` 
                                }}
                              />
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              {((scheme.total_value / clientDetails.summary.total_value) * 100).toFixed(1)}% of portfolio
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* All Holdings Table */}
                <Card className="bg-secondary/30 border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-primary" />
                      All Holdings ({clientDetails.holdings.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-2 px-3 text-xs font-medium text-muted-foreground">Scheme</th>
                            <th className="text-right py-2 px-3 text-xs font-medium text-muted-foreground">Units</th>
                            <th className="text-right py-2 px-3 text-xs font-medium text-muted-foreground">NAV</th>
                            <th className="text-right py-2 px-3 text-xs font-medium text-muted-foreground">Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {clientDetails.holdings.map((holding, index) => (
                            <tr key={index} className="border-b border-border/50">
                              <td className="py-3 px-3">
                                <p className="text-white line-clamp-1">{holding.scheme_name}</p>
                                <p className="text-xs text-muted-foreground">Folio: {holding.folio}</p>
                              </td>
                              <td className="py-3 px-3 text-right text-muted-foreground">
                                {formatNumber(holding.units)}
                              </td>
                              <td className="py-3 px-3 text-right text-muted-foreground">
                                ₹{formatNumber(holding.nav)}
                              </td>
                              <td className="py-3 px-3 text-right font-medium text-white">
                                {formatCurrency(holding.current_value)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                No data available
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ClientDashboardPage;
